import { Directive, Input, OnChanges, OnDestroy, OnInit, inject } from '@angular/core';
import { RecordRef } from '../../ai/features/record-ref';
import { NovaContext, NovaContextService } from './nova-context.service';

/**
 * Plugs a record context (contact / account / spotlight) into Nova while the
 * host element is alive, and unplugs it on destroy. Mirrors NovaMeetingContextDirective.
 *
 *   <ion-content [novaRecordContext]="{ kind:'contact', id, label }"> ... </ion-content>
 */
@Directive({
  selector: '[novaRecordContext]',
  standalone: true,
})
export class NovaRecordContextDirective implements OnInit, OnChanges, OnDestroy {
  @Input('novaRecordContext') record: RecordRef | null = null;

  readonly #svc = inject(NovaContextService);
  #ctx: NovaContext | null = null;

  ngOnInit(): void {
    this.#plug();
  }

  ngOnChanges(): void {
    // Re-plug whenever the bound record reference changes — including the
    // common case where it was null at init (async data load) and is now set.
    const current = this.#ctx?.kind === 'record' ? this.#ctx.record : null;
    if (current === this.record) return;
    if (this.#ctx) this.#unplug();
    this.#plug();
  }

  ngOnDestroy(): void {
    this.#unplug();
  }

  #plug(): void {
    if (!this.record || this.#ctx) return;
    this.#ctx = { kind: 'record', record: this.record };
    this.#svc.plug(this.#ctx);
  }

  #unplug(): void {
    if (!this.#ctx) return;
    this.#svc.unplug(this.#ctx);
    this.#ctx = null;
  }
}
